// src/components/mainpageSearch.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Input, Typography } from 'antd';
import { dbAPI, STORE_NAMES } from '../utils/db';
import ProductQuickView from './ProductQuickView';
import type {
  IgnitionCoilItem,
  SparkPlugItem,
  WiresItem,
  SensorItem,
  SteeringClockItem,
} from '../types/ProductItems';
import { Modal } from 'antd';
import SparkPlugForm from './forms/SparkPlugForm';
import IgnitionCoilForm from './forms/IgnitionCoilForm';
import WiresForm from './forms/WiresForm';
import SensorForm from './forms/SensorForm';
import SteeringClockForm from './forms/SteeringClockForm';
import RecentViews, { RecentItem } from './RecentViews';
const { Title } = Typography;

type AnyItem =
  | IgnitionCoilItem
  | SparkPlugItem
  | WiresItem
  | SensorItem
  | SteeringClockItem;

type FlatRow = {
  id: string;
  brand?: string;
  partNumber?: string;
  model?: string;
  original?: string;
  price?: number;
  raw: AnyItem;
  category: 'Катушки' | 'Свечи' | 'Проводки' | 'Датчики' | 'Шлейфы';
  
};

const toFlat = (arr: AnyItem[], category: FlatRow['category']): FlatRow[] =>
  arr.map((it: any) => ({
    id: it.id ?? it.key ?? String(Math.random()),
    brand: it.brand ?? '',
    partNumber: it.partNumber ?? '',
    model: it.model ?? '',
    original: it.original ?? '',
    price: it.price ?? undefined,
    category,
    raw: it,
  }));

  const formByCategory: Record<
    FlatRow['category'],
    React.FC<{ initialValues?: any | null; onAdd: (item: any) => void }>
  > = {
    'Катушки': IgnitionCoilForm,
    'Свечи': SparkPlugForm,
    'Проводки': WiresForm,
    'Датчики': SensorForm,
    'Шлейфы': SteeringClockForm,
  };

  const storeByCategory: Record<FlatRow['category'], keyof typeof STORE_NAMES> = {
    'Катушки': 'coils',
    'Свечи': 'sparkPlugs',
    'Проводки': 'wires',
    'Датчики': 'sensors',
    'Шлейфы': 'steeringClocks',
  };

const MainpageSearch: React.FC = () => {
  
  const [q, setQ] = useState('');
  const [all, setAll] = useState<FlatRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<(AnyItem & { category: string }) | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const RECENT_KEY = 'recentProducts';
  const [recent, setRecent] = useState<RecentItem[]>([]);

  // 1) загружаем все товары из IndexedDB один раз
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [coils, plugs, wires, sensors, clocks] = await Promise.all([
          dbAPI.getAll<IgnitionCoilItem>(STORE_NAMES.coils),
          dbAPI.getAll<SparkPlugItem>(STORE_NAMES.sparkPlugs),
          dbAPI.getAll<WiresItem>(STORE_NAMES.wires),
          dbAPI.getAll<SensorItem>(STORE_NAMES.sensors),
          dbAPI.getAll<SteeringClockItem>(STORE_NAMES.steeringClocks),
        ]);
        if (!mounted) return;
        setAll([
          ...toFlat(coils, 'Катушки'),
          ...toFlat(plugs, 'Свечи'),
          ...toFlat(wires, 'Проводки'),
          ...toFlat(sensors, 'Датчики'),
          ...toFlat(clocks, 'Шлейфы'),
        ]);
      } finally {
        if (mounted) setLoading(false);
      }
          try {
              const raw = localStorage.getItem(RECENT_KEY);
              if (raw) setRecent(JSON.parse(raw));
            } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 2) фильтрация «как в подразделах» по каждому символу
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return all.filter((item) =>
      [item.brand, item.partNumber, item.model, item.original, String(item.price), item.category]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [all, q]);

  // 3) формируем options для AutoComplete
  const options = useMemo(
    () =>
      filtered.slice(0, 50).map((it) => {
        const label = [
          it.brand,
          it.partNumber,
          it.model,
          it.original === 'original' ? 'Оригинал' : it.original === 'copy' ? 'Копия' : '',
          it.price != null ? `${it.price} ֏` : '',
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        return {
          value: label,               // что попадёт в инпут при выборе
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{label}</span>
              <span style={{ color: 'gray', marginLeft: 12 }}>({it.category})</span>
            </div>
          ),
          meta: { raw: (it as any).raw ?? it, category: it.category },
        };
      }),
    [filtered]
  );

  // 4) по выбору — переходим в нужный подраздел
  const handleSelect = (_value: string, option: any) => {
    const { raw, category } = option.meta || {};
    if (!raw) return;
  const thumb =
     Array.isArray(raw.images) && raw.images.length > 0 ? raw.images[0] : null;
  const entry: RecentItem = {
    id: raw.id || raw.key,
    category,
    brand: raw.brand ?? '',
    partNumber: raw.partNumber ?? '',
    model: raw.model ?? '',
    original: raw.original ?? '',
    price: typeof raw.price === 'number' ? raw.price : (raw.price ? Number(raw.price) : null),
    thumb,
    quantity:
      typeof raw.quantity === 'number'
        ? raw.quantity
        : raw.quantity != null
        ? Number(raw.quantity)
        : null,
  };
    setRecent(prev => {
     // без дублей: сначала убираем такой же
     const without = prev.filter(x => !(x.id === entry.id && x.category === entry.category));
     const next = [entry, ...without].slice(0, 30); // максимум 30
     localStorage.setItem(RECENT_KEY, JSON.stringify(next));
     return next;
   });  
    setSelected({ ...raw, category });
    setOpen(true);
    
  };

return (
  <div>
    <Title level={4}>🔍 Глобальный поиск по товарам</Title>

    <AutoComplete
      options={options}
      onSelect={handleSelect}
      onSearch={setQ}
      style={{ width: '100%' }}
      notFoundContent={loading ? 'Загрузка…' : 'Нет совпадений'}
      filterOption={false}
    >
      <Input placeholder="Введите бренд, номер детали, модель…" allowClear />
    </AutoComplete>

    <ProductQuickView
      open={open}
      item={selected}
      onClose={() => setOpen(false)}
      onEdit={() => {
        if (!selected) return;
        setEditItem(selected);
        setEditOpen(true);
        setOpen(false);
      }}
    />
    <Modal
      open={editOpen}
      onCancel={() => setEditOpen(false)}
      footer={null}
      title="Редактировать товар"
      width={580}
      maskClosable={false}
      destroyOnHidden
    >
      {editItem && (() => {
        const cat = editItem.category as FlatRow['category'];
        const FormComp = formByCategory[cat];
        const storeName = STORE_NAMES[storeByCategory[cat]];
        if (!FormComp || !storeName) return null;

        return (
          <FormComp
            initialValues={editItem}
            onAdd={async (updatedItem: any) => {
              await dbAPI.put(storeName, updatedItem);
              setAll(prev =>
                prev.map(row => row.id === updatedItem.id
                  ? {
                      ...row, // сохраняем всё остальное
                      // Обновляем те поля, которые нужны для отображения в списке поиска
                      brand: updatedItem.brand ?? row.brand,
                      partNumber: updatedItem.partNumber ?? row.partNumber,
                      model: updatedItem.model ?? row.model,
                      original: updatedItem.original ?? row.original,
                      price: updatedItem.price ?? row.price,
                      // Полный объект товара (raw) перезаписываем на свежий,
                      // чтобы форма всегда открывалась с актуальными данными
                      raw: { ...updatedItem }
                    }
                  : row
                )
              );
              setEditOpen(false);
            }}
          />
        );
      })()}
    </Modal>
    <RecentViews
      items={recent}
      onClear={() => {
        setRecent([]);
        localStorage.removeItem(RECENT_KEY);
      }}
    />
  </div>
);
};

export default MainpageSearch;
